import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// Storage keys
const POPUP_STORAGE_KEY = 'glowhealth_popup_shown';
const EMAIL_CAPTURED_KEY = 'glowhealth_email_captured';
const PAGE_VIEWS_KEY = 'glowhealth_page_views';
const SESSION_START_KEY = 'glowhealth_session_start';
const CTA_CLICKED_KEY = 'glowhealth_cta_clicked';
const ASSESSMENT_STARTED_KEY = 'glowhealth_assessment_started';
const ELIGIBILITY_OPENED_KEY = 'glowhealth_eligibility_opened';

// Configuration
const MIN_PAGE_VIEWS = 2;
const MIN_TIME_ON_SITE_MS = 45000; // 45 seconds
const SCROLL_THRESHOLD = 0.70; // 70% scroll
const POPUP_COOLDOWN_DAYS = 14;

// Pages where popup should NEVER show
const SUPPRESSED_PATHS = [
  '/intake',           // Assessment pages
  '/assessment',       // Assessment pages
  '/schedule',         // Calendly scheduling
  '/checkout',         // Stripe checkout
  '/pricing',          // Pricing page (high-intent)
  '/thank-you',        // Completion pages
];

// Utility: Check if popup is suppressed on current page
const isPopupSuppressed = (pathname) => {
  return SUPPRESSED_PATHS.some(path => pathname.startsWith(path));
};

// Utility: Track page views in sessionStorage
const trackPageView = () => {
  const currentViews = parseInt(sessionStorage.getItem(PAGE_VIEWS_KEY) || '0', 10);
  sessionStorage.setItem(PAGE_VIEWS_KEY, (currentViews + 1).toString());
  return currentViews + 1;
};

// Utility: Get session start time
const getSessionStart = () => {
  let sessionStart = sessionStorage.getItem(SESSION_START_KEY);
  if (!sessionStart) {
    sessionStart = Date.now().toString();
    sessionStorage.setItem(SESSION_START_KEY, sessionStart);
  }
  return parseInt(sessionStart, 10);
};

// Utility: Check if user has clicked any primary CTA
const hasClickedCTA = () => {
  return sessionStorage.getItem(CTA_CLICKED_KEY) === 'true';
};

// Utility: Check if assessment was started
const hasStartedAssessment = () => {
  return sessionStorage.getItem(ASSESSMENT_STARTED_KEY) === 'true';
};

// Utility: Check if eligibility modal was opened
const hasOpenedEligibility = () => {
  return sessionStorage.getItem(ELIGIBILITY_OPENED_KEY) === 'true';
};

// Utility: Check if email already captured
const isEmailCaptured = () => {
  return localStorage.getItem(EMAIL_CAPTURED_KEY) === 'true';
};

// Utility: Check popup cooldown
const isInCooldown = () => {
  const lastShown = localStorage.getItem(POPUP_STORAGE_KEY);
  if (lastShown) {
    const lastShownDate = new Date(parseInt(lastShown, 10));
    const daysSinceShown = (Date.now() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceShown < POPUP_COOLDOWN_DAYS;
  }
  return false;
};

// Export functions for other components to mark CTA interactions
export const markCTAClicked = () => {
  sessionStorage.setItem(CTA_CLICKED_KEY, 'true');
};

export const markAssessmentStarted = () => {
  sessionStorage.setItem(ASSESSMENT_STARTED_KEY, 'true');
};

export const markEligibilityOpened = () => {
  sessionStorage.setItem(ELIGIBILITY_OPENED_KEY, 'true');
};

const EmailCapturePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const hasTriggered = useRef(false);
  const location = useLocation();

  // Check ALL conditions for showing popup
  const shouldShowPopup = useCallback(() => {
    // 1. Suppressed on current page?
    if (isPopupSuppressed(location.pathname)) {
      return false;
    }

    // 2. Email already captured?
    if (isEmailCaptured()) {
      return false;
    }

    // 3. In cooldown period?
    if (isInCooldown()) {
      return false;
    }

    // 4. User clicked any primary CTA?
    if (hasClickedCTA()) {
      return false;
    }

    // 5. Assessment already started?
    if (hasStartedAssessment()) {
      return false;
    }

    // 6. Eligibility modal already opened?
    if (hasOpenedEligibility()) {
      return false;
    }

    // 7. Minimum page views?
    const pageViews = parseInt(sessionStorage.getItem(PAGE_VIEWS_KEY) || '0', 10);
    if (pageViews < MIN_PAGE_VIEWS) {
      return false;
    }

    // 8. Minimum time on site?
    const sessionStart = getSessionStart();
    const timeOnSite = Date.now() - sessionStart;
    if (timeOnSite < MIN_TIME_ON_SITE_MS) {
      return false;
    }

    return true;
  }, [location.pathname]);

  // Show popup function
  const showPopup = useCallback(() => {
    if (!hasTriggered.current && shouldShowPopup()) {
      hasTriggered.current = true;
      setIsVisible(true);
    }
  }, [shouldShowPopup]);

  // Track page views on route change
  useEffect(() => {
    trackPageView();
    getSessionStart(); // Initialize session start
  }, [location.pathname]);

  // Set up triggers
  useEffect(() => {
    // Reset trigger flag when conditions might have changed
    hasTriggered.current = false;

    // Don't set up triggers if on suppressed page
    if (isPopupSuppressed(location.pathname)) return;

    // Check conditions periodically (every 5 seconds after minimum time)
    const checkInterval = setInterval(() => {
      if (shouldShowPopup()) {
        showPopup();
        clearInterval(checkInterval);
      }
    }, 5000);

    // Scroll trigger - 70% of page with no CTA interaction
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      
      const scrollPosition = window.scrollY;
      const scrollPercentage = scrollPosition / scrollHeight;

      if (scrollPercentage >= SCROLL_THRESHOLD && shouldShowPopup()) {
        showPopup();
      }
    };

    // Exit intent (desktop only)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && shouldShowPopup()) {
        showPopup();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      clearInterval(checkInterval);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [location.pathname, shouldShowPopup, showPopup]);

  const handleClose = () => {
    setIsVisible(false);
    // Mark as shown for cooldown
    localStorage.setItem(POPUP_STORAGE_KEY, Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/leads/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName: firstName || null,
          source: 'hormone_guide_popup_low_intent',
          pageUrl: window.location.href
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSuccess(true);
      // Mark email as captured permanently
      localStorage.setItem(EMAIL_CAPTURED_KEY, 'true');
      localStorage.setItem(POPUP_STORAGE_KEY, Date.now().toString());
      
      // Auto-close after 4 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);

    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="popup-overlay" onClick={handleClose} data-testid="popup-overlay">
      <div className="popup-container popup-low-intent" onClick={(e) => e.stopPropagation()}>
        <button className="popup-close" onClick={handleClose} aria-label="Close" data-testid="popup-close-btn">
          <X size={18} />
        </button>

        {!isSuccess ? (
          <>
            <div className="popup-header">
              <h2 className="popup-title">Not ready to start yet?</h2>
              <p className="popup-subtitle">
                Get a short hormone health guide we share with women who are just beginning to explore their symptoms.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="popup-form">
              <div className="popup-field">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="popup-input"
                  required
                  data-testid="popup-email-input"
                />
              </div>
              <div className="popup-field">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="popup-input"
                  data-testid="popup-firstname-input"
                />
              </div>
              
              {error && <p className="popup-error">{error}</p>}
              
              <button 
                type="submit" 
                className="popup-submit"
                disabled={isSubmitting}
                data-testid="popup-submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Send me the guide'}
              </button>
            </form>

            <p className="popup-privacy">
              No spam. You can unsubscribe anytime.
            </p>
          </>
        ) : (
          <div className="popup-success">
            <div className="popup-success-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="26" stroke="#CBB26A" strokeWidth="2"/>
                <path d="M18 28L25 35L38 22" stroke="#CBB26A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="popup-success-title">Check your inbox!</h2>
            <p className="popup-success-text">
              Your guide is on its way.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCapturePopup;
